/**
 * State a page-level one-shot trigger (Exit intent, Duration) keeps for the
 * lifetime of its registration: it has to survive a conditional rebind, where
 * the engine tears the trigger down and calls its handler again with the SAME
 * trigger tuple, but not the interaction being removed and registered again.
 *
 * The engine owns the second half: `IX3Engine.remove()` (which `destroy()` and
 * `update()` both go through) resets the state of every trigger it drops. A
 * host that re-registers the same interaction objects — the Designer canvas
 * iframe does on every Preview entry — therefore starts from a clean slate,
 * where module-level state in the trigger plugin would outlive it.
 */
export type PageLevelTriggerState = {
  /** The one-shot has fired; a later binding of this trigger stays inert. */
  fired: boolean;
  /**
   * The one-shot claimed its interaction's moment: it fired and no sibling
   * one-shot had claimed it first. Separate from `fired`, which a one-shot that
   * stood down for a sibling also sets: when a rebind hands the winner's fire
   * back, the loser must not keep it from playing.
   */
  won: boolean;
  /**
   * Absolute deadline (epoch ms) of a `timeOnSite` Duration countdown, set on
   * first bind so a rebind keeps measuring from the same point.
   */
  deadline: number | undefined;
};

const states = new WeakMap<object, PageLevelTriggerState>();

export function getPageLevelTriggerState(
  trigger: object
): PageLevelTriggerState {
  let state = states.get(trigger);
  if (!state) {
    state = {fired: false, won: false, deadline: undefined};
    states.set(trigger, state);
  }
  return state;
}

/**
 * Whether `trigger` claimed its interaction's moment. Never creates state, so
 * it can be asked of any trigger: one that is not page-level reads false.
 */
export function hasPageLevelTriggerWon(trigger: object): boolean {
  return states.get(trigger)?.won === true;
}

/**
 * Records that `trigger` claimed its interaction's moment. Never creates state
 * for a trigger that has none (any non-page-level trigger).
 */
export function claimPageLevelTriggerWin(trigger: object): void {
  const state = states.get(trigger);
  if (state) state.won = true;
}

export function resetPageLevelTriggerState(trigger: object): void {
  states.delete(trigger);
}

/**
 * Hands the one-shot back: a trigger delay that was still counting down when
 * its binding was torn down never played, so the fire it came from was not
 * spent. Without this a conditional rebind inside the delay window leaves the
 * trigger inert with nothing played. Never creates state for a trigger that
 * has none (any non-page-level trigger).
 */
export function releasePageLevelTriggerFired(trigger: object): void {
  const state = states.get(trigger);
  if (!state) return;
  state.fired = false;
  // Its claim on the moment goes with it, so a sibling that stood down for it
  // does not keep it from playing when it fires again.
  state.won = false;
}
